"use client";
import AboutAuthor from '@/components/ui/AboutAuthor';
import Button from '@/components/ui/Button';
import { extractTextFromHTML } from '@/lib/utils/blog-content-reader';
import { ArrowLeft, Calendar, Eye, Tag, Volume2, VolumeX } from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';
import { FcGoogle } from 'react-icons/fc';

interface BlogFormData {
  title: string;
  publishDate: string;
  author: string;
  tags: string[];
  excerpt: string;
  content: string;
  featuredImage: string;
  featuredImageAlt?: string;
}

interface BlogPreviewProps {
  formData: BlogFormData;
  onBackToEdit: () => void;
  hideRecentPosts?: boolean;
  hideHeader?: boolean;
}

export default function BlogPreview({
  formData,
  onBackToEdit,
  hideRecentPosts = false,
  hideHeader = false
}: BlogPreviewProps) {
  const [isReading, setIsReading] = useState(false);

  const formattedDate = formData.publishDate
    ? new Date(formData.publishDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
    : 'Draft';

  const calculateReadTime = (content: string): number => {
    const plainText = extractTextFromHTML(content || '');
    const wordCount = plainText.split(/\s+/).filter(word => word.length > 0).length;
    return Math.max(1, Math.ceil(wordCount / 200)); // 200 words per minute
  };

  const readTime = calculateReadTime(formData.content);

  const toggleReadAloud = () => {
    if (isReading) {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      setIsReading(false);
    } else {
      if (window.speechSynthesis) {
        const plainText = extractTextFromHTML(formData.content || '');
        const utterance = new SpeechSynthesisUtterance(plainText);
        utterance.onend = () => setIsReading(false);
        utterance.onerror = () => setIsReading(false);
        window.speechSynthesis.speak(utterance);
        setIsReading(true);
      }
    }
  };

  const shareBlogPreview = () => {
    const url = window.location.href;

    if (navigator.share) {
      navigator.share({
        title: formData.title,
        text: formData.excerpt,
        url
      }).catch(() => {
        navigator.clipboard.writeText(url);
      });
    } else {
      navigator.clipboard.writeText(url);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {!hideHeader && (
        <>
          {/* Preview Header Banner */}
          <div className="bg-gray-200/50 backdrop-blur-lg border border-gray-200/50 rounded-md sticky top-0 z-50">
            <div className="container mx-auto px-4 py-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center space-x-2">
                  <Eye className="h-5 w-5 text-yellow-600" />
                  <span className="text-sm font-medium text-yellow-800">Preview Mode</span>
                  <span className="text-xs text-yellow-600 hidden sm:inline">
                    This is how your blog will look when published
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={onBackToEdit}
                    className="px-4 py-2 bg-yellow-600 text-white text-sm rounded-md hover:bg-yellow-700 transition-colors"
                  >
                    Back to Edit
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Header Navigation */}
          <div className="bg-white shadow-sm">
            <div className="container mx-auto px-4 py-4">
              <button
                onClick={onBackToEdit}
                className="inline-flex items-center text-gray-600 hover:text-gray-800 transition-colors font-medium"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Editor
              </button>
            </div>
          </div>
        </>
      )}

      {/* Main Content Grid */}
      <div
        className={
          hideRecentPosts
            ? 'grid grid-cols-1 custom-container mx-auto min-h-screen px-4 py-8'
            : 'grid grid-cols-1 lg:grid-cols-3 gap-8 custom-container mx-auto min-h-screen px-4 py-8'
        }
      >
        {/* Main Article */}
        <article
          className={
            hideRecentPosts ? 'h-fit space-y-8' : 'lg:col-span-2 h-fit space-y-8'
          }
        >
          {/* Hero Section */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {formData.featuredImage && formData.featuredImage.trim() !== '' && (
              <div className="relative h-64 md:h-96">
                <Image
                  src={formData.featuredImage}
                  alt={formData.featuredImageAlt || formData.title}
                  fill
                  className="object-cover"
                  priority
                  onError={(e) => {
                    const container = e.currentTarget.parentElement;
                    if (container) {
                      container.style.display = 'none';
                    }
                  }}
                />
              </div>
            )}

            <div className="p-6 md:p-8 lg:p-10">
              {/* Meta Information */}
              <div className="flex flex-wrap justify-between items-center gap-4 text-sm text-gray-600 mb-5 border-b border-[#ececec] pb-2 font-medium">
                <div className="flex flex-wrap items-center text-sm text-gray-600 font-medium">
                  <div className="flex items-center gap-4 md:gap-4 divide-x divide-[#ececec]">
                    {/* Date */}
                    <div className="flex items-center justify-center gap-2 px-2">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <span>{formattedDate}</span>
                    </div>

                    {/* Source preferences */}
                    <div className="flex items-center gap-2 justify-center px-2">
                    <a
  href="https://www.google.com/preferences/source?q=makemybrandlabs.com"
  target="_blank"
  rel="noopener noreferrer"
  className="sparkle-border relative inline-flex flex-col nowrap items-start hover:scale-105 bg-red-100 -skew-x-12 px-3 py-1.5 text-left text-sm bg-gray-50 hover:bg-gray-100 transition-colors no-underline text-inherit overflow-hidden"
  title="Your Trusted Source for Insights"
>
  <span className="text-xs text-gray-600 skew-x-12 flex items-center gap-2">
  <FcGoogle  className="h-4 w-4 text-red-500" /> Your Trusted Source for Insights

  </span>
</a>
                    </div>
                  </div>
                </div>

                {/* Tags */}
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-3">
                    {formData.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-semibold bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors"
                      >
                        <Tag className="h-3 w-3 mr-1" />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Title */}
              <h1 className="text-3xl sm:text-4xl font-semibold text-[#1b1b1b] mb-4 leading-tight tracking-tight">
                {formData.title || 'Blog Title'}
              </h1>

              {/* Excerpt */}
              {formData.excerpt && (
                <p className="text-[1.03rem] text-[#1b1b1b] leading-relaxed mb-8 leading-snug">
                  {formData.excerpt}
                </p>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={toggleReadAloud}
                  variant={isReading ? "secondary" : "primary"}
                  size="lg"
                  className="flex !flex-nowrap"
                >
                  {isReading ? (
                    <>
                      <VolumeX className="h-4 w-4" />
                      Stop Reading
                    </>
                  ) : (
                    <>
                      <Volume2 className="h-4 w-4" />
                      Read Aloud
                    </>
                  )}
                </Button>
                <Button
                  onClick={shareBlogPreview}
                  variant="secondary"
                  size="md"
                >
                  Share Article
                </Button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white rounded-lg shadow-sm p-6 md:p-8 lg:p-10">
            {/* SEO link visualization (dofollow / nofollow badges) */}
            <style
              dangerouslySetInnerHTML={{
                __html: `
                  .blog-detail-content a[rel~="nofollow"] {
                    background-color: rgb(255, 131, 131) !important;
                    border-radius: 4px;
                    padding: 0 2px;
                    position: relative;
                  }
                  .blog-detail-content a[href]:not([rel~="nofollow"]) {
                    background-color: rgb(107, 255, 159) !important;
                    border-radius: 4px;
                    padding: 0 2px;
                    position: relative;
                  }
                  .blog-detail-content a {
                    position: relative;
                  }
                  .blog-detail-content a::after {
                    position: absolute;
                    top: -24px;
                    left: -8px;
                    padding: 5px 6px;
                    border-radius: 4px;
                    font-size: 11px;
                    line-height: 1;
                    z-index: 10;
                    pointer-events: none;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.15);
                    color: #1a1a1a;
                    font-weight: 600;
                  }
                  .blog-detail-content a[rel~="nofollow"]::after {
                    content: 'nofollow';
                    background: rgb(255, 96, 96);
                  }
                  .blog-detail-content a[href]:not([rel~="nofollow"])::after {
                    content: 'dofollow';
                    background: rgb(70, 255, 95);
                  }
                `
              }}
            />
            <div
              className="blog-detail-content prose prose-lg prose-gray max-w-none
                           prose-headings:font-bold prose-headings:text-gray-900
                           prose-h1:text-2xl prose-h1:lg:text-3xl prose-h1:mb-6
                           prose-h2:text-xl prose-h2:lg:text-2xl prose-h2:mb-4
                           prose-h3:text-lg prose-h3:lg:text-xl prose-h3:mb-3
                           prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
                           prose-li:text-gray-700 prose-li:leading-relaxed
                           prose-strong:font-semibold prose-strong:text-gray-900
                           prose-a:text-blue-600 prose-a:hover:text-blue-800 prose-a:transition-colors"
              dangerouslySetInnerHTML={{ __html: formData.content || '<p class=\"text-gray-500 italic\">No content yet. Add some content in edit mode.</p>' }}
            />
          </div>

          {/* About Author & Footer Combined (match live) */}
          {formData.author && (
            <AboutAuthor
              authorName={formData.author}
              formattedDate={formattedDate}
              footerActions={
                <>
                  <Button
                    href="/blogs"
                    variant="secondary"
                    size="md"
                  >
                    View All Blogs
                  </Button>
                  <Button
                    onClick={toggleReadAloud}
                    variant={isReading ? "secondary" : "primary"}
                    size="md"
                    className="flex flex-row items-center nowrap gap-2 !leading-none"
                  >
                    {isReading ? (
                      <span className="text-sm font-medium">Stop Reading</span>
                    ) : (
                      <span className="text-sm font-medium">Read Aloud</span>
                    )}
                  </Button>
                  <Button
                    onClick={shareBlogPreview}
                    variant="primary"
                    size="md"
                  >
                    Share Article
                  </Button>
                </>
              }
            />
          )}
        </article>

        {!hideRecentPosts && (
          <aside className="lg:col-span-1 space-y-8 sticky top-24 self-start">
            <div className="bg-white rounded-lg shadow-sm p-6 lg:p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <Tag className="h-5 w-5 mr-3 text-blue-600" />
                Recent Posts (Preview)
              </h3>
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                    <div className="flex gap-3">
                      <div className="flex-shrink-0">
                        <div className="w-15 h-15 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center overflow-hidden">
                          <span className="text-blue-600 text-xs font-semibold">
                            {formData.title?.charAt(0).toUpperCase() || 'B'}
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-2 leading-tight">
                          {formData.title || 'Blog title preview'}
                        </h4>
                        <p className="text-xs text-gray-600 flex items-center gap-2">
                          <span>{formattedDate}</span>
                          <span>•</span>
                          <span>{readTime} min read</span>
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
